import { useEffect, useRef, useState } from 'react';
import { signalAttenuation } from '@omnialera/ori-signal';
import { useScan } from '../lib/useScan.js';
import { useT } from '../lib/i18n.jsx';
import { RegionOverlay, SkinViewport, Trace } from '../components/canvas.jsx';
import { Reading } from '../components/reading.jsx';
import { Notice, Stat, Why, percent } from '../components/ui.jsx';

export default function Scan({ record, onResult, donateState }) {
  const t = useT();
  const [source, setSource] = useState('simulated');
  const [showDiagnostics, setShowDiagnostics] = useState(false);
  const videoRef = useRef(null);
  const handledRef = useRef(null);

  const scan = useScan({ monkTone: record.monkTone, source, videoRef });
  const { live, finished, diagnostics } = scan;
  const result = live.result;

  // Hand the finished reading up exactly once.
  useEffect(() => {
    if (!result || handledRef.current === result) return;
    handledRef.current = result;
    onResult(result);
  }, [result, onResult]);

  const attenuation = Math.round(signalAttenuation(record.monkTone ?? 1) * 100);

  const chooseCamera = async () => {
    const ok = await scan.enableCamera();
    if (ok) setSource('camera');
  };

  const heading = finished
    ? result?.locked
      ? t('scan.title.locked')
      : t('scan.title.failed')
    : scan.scanning
      ? t('scan.title.running')
      : t('scan.title.idle');

  return (
    <section>
      <div className="vh">
        <p className="eyebrow">{t('scan.eyebrow')}</p>
        <h1>{heading}</h1>
        <p>{t('scan.intro')}</p>
      </div>

      {scan.error ? (
        <Notice kind="warn" title="Camera">
          {scan.error}
        </Notice>
      ) : null}

      <div className="scanstage">
        <div>
          {source === 'camera' ? (
            <div className="viewport">
              <video ref={videoRef} playsInline muted />
              <RegionOverlay
                getRegionBoxes={scan.getRegionBoxes}
                tracked={live.faceTracked}
                active={scan.scanning}
              />
            </div>
          ) : (
            <SkinViewport monkTone={record.monkTone} progress={live.progress} scanning={scan.scanning} />
          )}

          <Trace getTrace={scan.getTrace} active={scan.scanning} />

          {source === 'camera' && (scan.scanning || scan.cameraReady) ? (
            <p className={`advice ${live.advice.severity}`} role="status" aria-live="polite">
              {t(`advice.${live.advice.code}`)}
            </p>
          ) : null}

          {scan.scanning && live.rejected > 0 ? (
            <p className="advice warn">
              {live.rejected === 1 ? t('scan.skipped.one') : t('scan.skipped', { count: live.rejected })}
            </p>
          ) : null}
        </div>

        <div className="stack">
          <div className="trio">
            <Stat label={t('stat.elapsed')} value={live.seconds.toFixed(1)} unit={t('unit.s')} />
            <Stat label={t('stat.clarity')} value={live.prominence === null ? '—' : live.prominence.toFixed(1)} />
            <Stat label={t('stat.agreement')} value={live.agreement === null ? '—' : percent(live.agreement)} />
          </div>

          {scan.scanning && live.bpm !== null && !finished ? (
            <p className="advice" style={{ color: 'var(--muted)' }}>
              {t('scan.guess', { bpm: Math.round(live.bpm) })}{' '}
              {live.methodsAgree === false ? t('scan.guess.disagree') : t('scan.guess.settling')}
            </p>
          ) : null}

          {finished && result ? (
            <Reading
              result={result}
              tone={record.monkTone}
              donateState={donateState}
              getSpectrum={scan.getSpectrum}
              live={live}
              scanning={scan.scanning}
            />
          ) : null}

          <div className="btnrow">
            <button
              type="button"
              className="btn"
              onClick={() => {
                handledRef.current = null;
                scan.start();
              }}
              disabled={scan.scanning}
            >
              {scan.scanning ? t('scan.running') : finished ? t('scan.again') : t('scan.start')}
            </button>
            {source === 'simulated' ? (
              <button type="button" className="btn ghost" onClick={chooseCamera} disabled={scan.scanning}>
                {t('scan.useCamera')}
              </button>
            ) : (
              <button
                type="button"
                className="btn ghost"
                onClick={() => setSource('simulated')}
                disabled={scan.scanning}
              >
                {t('scan.useSimulated')}
              </button>
            )}
          </div>

          <p style={{ fontSize: '0.92rem', color: 'var(--faint)', margin: 0, lineHeight: 1.55 }}>
            {source === 'camera'
              ? t('scan.privacy')
              : t('scan.simulated', { tone: record.monkTone ?? 1, attenuation })}
          </p>

          <Diagnostics
            open={showDiagnostics}
            onToggle={() => setShowDiagnostics((v) => !v)}
            diagnostics={diagnostics}
            live={live}
          />
        </div>
      </div>

      <Why>
        <strong>
          A system that refuses to answer is trustworthy. A system that answers wrong with full confidence is worse
          than no system
        </strong>{' '}
        — and that is what is shipping today. Set your tone to 9 and scan: Ori will give up, and it will show you the
        confident wrong number a conventional fixed-window estimator would have printed from the very same data.
      </Why>
    </section>
  );
}

/**
 * The diagnostics drawer.
 *
 * Most health apps show a confidence percentage, which is a single number
 * asking to be trusted. This shows the disagreement itself: what each method
 * believes, region by region. If they differ, you can see it, and so can a
 * clinician looking over your shoulder.
 */
function Diagnostics({ open, onToggle, diagnostics, live }) {
  const t = useT();
  const has = diagnostics.methods.length > 0;

  return (
    <div className="card" style={{ padding: '14px 16px' }}>
      <button
        type="button"
        className="btn ghost diagtoggle"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span>{t('diag.toggle')}</span>
        <span aria-hidden="true">{open ? '−' : '+'}</span>
      </button>

      {open ? (
        !has ? (
          <p style={{ fontSize: '0.92rem', color: 'var(--faint)', margin: '12px 0 0' }}>{t('diag.empty')}</p>
        ) : (
          <div style={{ marginTop: '12px' }}>
            {/* Its own scroll container. Four columns of monospaced numbers do
                not fit a 390px phone, and without this the table paints past
                the card and the whole page scrolls sideways — invisible to a
                viewport check that never opens the drawer. */}
            <div className="tscroll" style={{ marginTop: 0 }}>
            <table className="diag">
              <thead>
                <tr>
                  <th scope="col">{t('diag.method')}</th>
                  <th scope="col">{t('diag.reads')}</th>
                  <th scope="col">{t('diag.from')}</th>
                </tr>
              </thead>
              <tbody>
                {diagnostics.methods.map((m) => (
                  <tr key={m.method}>
                    <td>{t(`diag.method.${m.method}`)}</td>
                    <td className="mono">
                      {m.bpm === null ? '—' : `${Math.round(m.bpm)} ${t('unit.bpm')}`}
                    </td>
                    <td className="mono">
                      {m.samples === 1 ? t('diag.regions.one') : t('diag.regions', { count: m.samples })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>

            <p className="advice" style={{ color: diagnostics.agree === false ? 'var(--pulse)' : 'var(--signal)' }}>
              {diagnostics.agree === false
                ? t('diag.disagree', { spread: Math.round(diagnostics.spreadBpm ?? 0) })
                : t('diag.agree', { spread: Math.round(diagnostics.spreadBpm ?? 0) })}
            </p>

            <dl className="kv">
              <div>
                <dt>{t('diag.framesUsed')}</dt>
                <dd className="mono">
                  {live.samples} / {live.samples + live.rejected} ({percent(live.acceptance)})
                </dd>
              </div>
              <div>
                <dt>{t('diag.harmonic')}</dt>
                <dd className="mono">{live.harmonic === null ? '—' : live.harmonic.toFixed(2)}</dd>
              </div>
              <div>
                <dt>{t('diag.spread')}</dt>
                <dd className="mono">
                  {live.spreadBpm === null ? '—' : `${live.spreadBpm.toFixed(1)} ${t('unit.bpm')}`}
                </dd>
              </div>
              <div>
                <dt>{t('diag.perfusion')}</dt>
                <dd className="mono">
                  {live.perfusion === null ? '—' : `${(live.perfusion * 1000).toFixed(1)} ×10⁻³`}
                </dd>
              </div>
            </dl>
          </div>
        )
      ) : null}
    </div>
  );
}
